/**
 * Owner: Masa Bayu
 * File: pdf-pages.ts
 * Date:Time: 2026-09-30 14:30
 * Description: Consecutive PDF pages into one Document IR, in page order.
 */

import { createQXDocumentIR, type QXDocumentIR, type QXDocumentRaster } from '@qx/qx-document-ir';
import { advanceUnitsForText, resolvePdfFontGlyphWidths } from './pdf-glyph-widths';
import { parsePdfLayoutToIR, type QXPdfLayoutPageInput } from './pdf-layout-parse';
import type { QXPdfObjectOrderImport, QXPdfOrderedObject, QXPdfOrderBox } from './pdf-object-order';
import { decodedPdfRaster } from './pdf-raster';

const latin1 = new TextDecoder('latin1');

type PageDict = {
  readonly width: number;
  readonly height: number;
  readonly contentsId: number;
  readonly names: ReadonlyMap<string, number>;
};

function bytesOf(text: string): Uint8Array {
  return Uint8Array.from(text, (char) => char.charCodeAt(0));
}

function indexOfBytes(haystack: Uint8Array, needle: Uint8Array, from = 0): number {
  const last = haystack.length - needle.length;
  for (let index = from; index <= last; index += 1) {
    let matched = true;
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (haystack[index + offset] !== needle[offset]) {
        matched = false;
        break;
      }
    }
    if (matched) return index;
  }
  return -1;
}

function textOf(file: Uint8Array, start: number, end: number): string {
  return latin1.decode(file.subarray(start, end));
}

function objectDict(file: Uint8Array, id: number): string {
  const marked = indexOfBytes(file, bytesOf(`\n${String(id)} 0 obj`));
  if (marked < 0) throw new Error(`PDF object ${String(id)} is missing`);
  const slice = file.subarray(marked + 1);
  const end = indexOfBytes(slice, bytesOf('endobj'));
  return textOf(slice, 0, end < 0 ? slice.length : end);
}

function readStream(file: Uint8Array, id: number): { readonly dict: string; readonly stream: Uint8Array | null } {
  const marked = indexOfBytes(file, bytesOf(`\n${String(id)} 0 obj`));
  const slice = file.subarray(marked + 1);
  const streamAt = indexOfBytes(slice, bytesOf('stream'));
  const endAt = indexOfBytes(slice, bytesOf('endobj'));
  if (streamAt < 0 || (endAt >= 0 && endAt < streamAt)) {
    return { dict: textOf(slice, 0, endAt < 0 ? slice.length : endAt), stream: null };
  }
  const dict = textOf(slice, 0, streamAt);
  const length = dict.match(/\/Length\s+(\d+)/);
  if (!length?.[1]) throw new Error(`PDF object ${String(id)} stream has no length`);
  let dataAt = streamAt + 'stream'.length;
  if (slice[dataAt] === 0x0d) dataAt += 1;
  if (slice[dataAt] === 0x0a) dataAt += 1;
  return { dict, stream: slice.subarray(dataAt, dataAt + Number(length[1])) };
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index] ?? 0);
  return btoa(binary);
}

function pdfName(raw: string): string {
  return raw.replace(/#([0-9A-Fa-f]{2})/g, (_, hex: string) => String.fromCharCode(Number.parseInt(hex, 16)));
}

function literalOf(block: string): string {
  let text = '';
  for (const item of block.matchAll(/\((?:\\.|[^)\\])*\)/g)) {
    text += (item[0] ?? '').slice(1, -1).replace(/\\(.)/g, '$1');
  }
  return text;
}

/**
 * Locate the /Pages tree node and read /Kids by structure, not token order.
 * Prefer the dictionary spanning << … >> around /Type /Pages so large Kids
 * arrays are not truncated by a fixed character window.
 */
function pageKids(fileText: string): number[] {
  const markers = ['/Type /Pages', '/Type/Pages'] as const;
  let at = -1;
  for (const marker of markers) {
    at = fileText.indexOf(marker);
    if (at >= 0) break;
  }
  if (at < 0) throw new Error('PDF has no page tree');

  const dictStart = fileText.lastIndexOf('<<', at);
  const dictEnd = fileText.indexOf('>>', at);
  const window =
    dictStart >= 0 && dictEnd > dictStart
      ? fileText.slice(dictStart, dictEnd + 2)
      : fileText.slice(Math.max(0, at - 240), Math.min(fileText.length, at + 240));
  const kids = window.match(/\/Kids\s*\[([^\]]+)\]/);
  const ids = [...(kids?.[1] ?? '').matchAll(/(\d+)\s+0\s+R/g)].map((item) => Number(item[1]));
  if (ids.length === 0) throw new Error('PDF page tree has no pages');
  return ids;
}

/** Page object ids from the PDF page tree — shared by Writer import entry and Eyes. */
export function listPdfPageTreeKids(bytes: Uint8Array): number[] {
  const file = Uint8Array.from(bytes);
  const fileText = textOf(file, 0, file.length);
  return pageKids(fileText);
}

/** Page count from the PDF page tree — shared by Writer import entry and Eyes. */
export function countPdfPageTree(bytes: Uint8Array): number {
  return listPdfPageTreeKids(bytes).length;
}

function pageDict(file: Uint8Array, id: number): PageDict {
  const dict = objectDict(file, id);
  const box = dict.match(/\/MediaBox\s*\[\s*[\d.]+\s+[\d.]+\s+([\d.]+)\s+([\d.]+)\s*\]/);
  const width = Number(box?.[1] ?? 0);
  const height = Number(box?.[2] ?? 0);
  const contentsId = Number(dict.match(/\/Contents\s+(\d+)\s+0\s+R/)?.[1] ?? 0);
  if (width <= 0 || height <= 0 || !contentsId) throw new Error(`PDF page ${String(id)} is incomplete`);
  const resourceId = Number(dict.match(/\/Resources\s+(\d+)\s+0\s+R/)?.[1] ?? 0);
  const resources = resourceId ? objectDict(file, resourceId) : dict;
  const xObjectId = Number(resources.match(/\/XObject\s+(\d+)\s+0\s+R/)?.[1] ?? 0);
  const source = xObjectId ? objectDict(file, xObjectId) : resources;
  const names = new Map<string, number>();
  for (const item of source.matchAll(/\/(Image\d+|F\d+)\s+(\d+)\s+0\s+R/g)) {
    names.set(item[1] ?? '', Number(item[2]));
  }
  return { width, height, contentsId, names };
}

type TdShowLine = { readonly x: number; readonly y: number; readonly text: string };

/**
 * BT/ET blocks positioned only with Td (no Tm) show each string at the
 * accumulated line matrix · PDF-spec identity start at (0,0).
 */
function parseTdShowLines(block: string): TdShowLine[] {
  const out: TdShowLine[] = [];
  let x = 0;
  let y = 0;
  for (const item of block.matchAll(/([-\d.]+)\s+([-\d.]+)\s+Td|\((?:\\.|[^)\\])*\)\s*Tj/g)) {
    const raw = item[0] ?? '';
    if (/Td\s*$/.test(raw)) {
      x += Number(item[1]);
      y += Number(item[2]);
      continue;
    }
    const text = raw.slice(1, raw.lastIndexOf(')')).replace(/\\(.)/g, '$1').trim();
    if (text.length > 0) out.push({ x, y, text });
  }
  return out;
}

/**
 * Reads consecutive pages from one uncompressed PDF.
 * Page order is the page tree order. A shared image keeps one asset id.
 */
export function importPdfPages(bytes: Uint8Array, pageNumbers: readonly number[]): QXPdfObjectOrderImport {
  if (pageNumbers.length === 0 || pageNumbers.some((page) => !Number.isInteger(page) || page < 1)) {
    throw new Error('pageNumbers must be positive integers');
  }
  const file = Uint8Array.from(bytes);
  const fileText = textOf(file, 0, file.length);
  const kids = pageKids(fileText);
  if (kids.length !== pageNumbers.length) throw new Error('PDF page count does not match the requested pages');
  const objects: QXPdfOrderedObject[] = [];
  const pages: QXPdfLayoutPageInput[] = [];
  const rasters = new Map<string, QXDocumentRaster>();
  kids.forEach((kid, index) => {
    const pageNumber = pageNumbers[index] ?? 0;
    const page = pageDict(file, kid);
    const content = readStream(file, page.contentsId);
    if (!content.stream || /\/Filter\b/.test(content.dict)) throw new Error('PDF content stream is not plain text');
    const stream = textOf(content.stream, 0, content.stream.length);
    const blocks: QXPdfLayoutPageInput['blocks'][number][] = [];
    let sourceOrder = 0;
    const push = (object: QXPdfOrderedObject, block: QXPdfLayoutPageInput['blocks'][number]) => {
      objects.push(object);
      blocks.push(block);
      sourceOrder += 1;
    };
    const images = [...stream.matchAll(/([-\d.]+)\s+0\s+0\s+([-\d.]+)\s+([-\d.]+)\s+([-\d.]+)\s+cm\s*\/(Image\d+)\s+Do/g)];
    const texts = [...stream.matchAll(/BT([\s\S]*?)ET/g)];
    const events = [
      ...images.map((item) => ({ at: item.index ?? 0, kind: 'image' as const, item })),
      ...texts.map((item) => ({ at: item.index ?? 0, kind: 'text' as const, item })),
    ].sort((left, right) => left.at - right.at);
    for (const event of events) {
      if (event.kind === 'image') {
        const width = Number(event.item[1]);
        const height = Number(event.item[2]);
        const name = event.item[5] ?? '';
        if (width < 80 || height < 80) {
          const smallId = page.names.get(name) ?? 0;
          const raster = smallId ? decodedPdfRaster(file, smallId) : null;
          if (!raster?.mask) continue;
          const sourceObjectId = `p${String(pageNumber)}_${name}`;
          const assetId = `page-${String(pageNumber)}/${name}`;
          const bbox: QXPdfOrderBox = {
            x: Number(event.item[3]),
            y: page.height - Number(event.item[4]) - height,
            width,
            height,
          };
          rasters.set(sourceObjectId, raster);
          push(
            { type: 'raster', sourceObjectId, assetId, sourcePage: pageNumber, sourceOrder, zOrder: sourceOrder, bbox, samples: raster.samples, maskSamples: raster.mask.samples },
            { id: sourceObjectId, kind: 'image', assetId, bbox },
          );
          continue;
        }
        const objectId = page.names.get(name) ?? 0;
        if (!objectId) throw new Error(`PDF image ${name} has no object`);
        const image = readStream(file, objectId);
        const head = image.stream;
        if (!head || head[0] !== 0xff || head[1] !== 0xd8 || head[2] !== 0xff) {
          throw new Error(`PDF object ${String(objectId)} is not a JPEG image`);
        }
        const jpeg = Uint8Array.from(head);
        const bbox: QXPdfOrderBox = {
          x: Number(event.item[3]),
          y: page.height - Number(event.item[4]) - height,
          width,
          height,
        };
        const sourceObjectId = `p${String(pageNumber)}_obj_${String(objectId)}`;
        const assetId = `asset_${String(objectId)}`;
        push(
          { type: 'image', sourceObjectId, assetId, sourcePage: pageNumber, sourceOrder, zOrder: sourceOrder, bbox, jpeg },
          { id: sourceObjectId, kind: 'image', assetId, text: `data:image/jpeg;base64,${base64(jpeg)}`, bbox },
        );
        continue;
      }
      const block = event.item[1] ?? '';
      const face = block.match(/\/(F\d+)\s+([\d.]+)\s+Tf/);
      const place = block.match(/1\s+0\s+0\s+1\s+([-\d.]+)\s+([-\d.]+)\s+Tm/);
      const text = literalOf(block).trim();
      if (!face?.[1] || !face[2]) continue;
      const showLines =
        place?.[1] && place[2]
          ? /[A-Za-z]{3,}/.test(text)
            ? [{ x: Number(place[1]), y: Number(place[2]), text }]
            : []
          : parseTdShowLines(block).filter((line) => /[A-Za-z]{3,}/.test(line.text));
      if (showLines.length === 0) continue;
      const fontId = page.names.get(face[1]) ?? 0;
      if (!fontId) throw new Error(`PDF font ${face[1]} has no object`);
      const font = objectDict(file, fontId);
      const base = font.match(/\/BaseFont\s*\/([^\s/]+)/)?.[1];
      if (!base) throw new Error('PDF font has no BaseFont');
      const fontName = pdfName(base);
      const glyph = resolvePdfFontGlyphWidths(font, (id) => objectDict(file, id));
      const size = Number(face[2]);
      const showingIndex = [...stream.slice(0, event.at).matchAll(/BT/g)].length;
      const bold = fontName.endsWith(',Bold') || fontName.endsWith('-Bold') || fontName.endsWith(' Bold');
      const italic = /Italic/.test(fontName);
      showLines.forEach((line, lineIndex) => {
        const units = advanceUnitsForText(glyph, line.text);
        const bbox: QXPdfOrderBox = {
          x: line.x,
          y: page.height - line.y - size,
          width: (units * size) / 1000,
          height: size,
        };
        const sourceObjectId =
          showLines.length === 1
            ? `txt_${face[1]}_${String(showingIndex)}`
            : `txt_${face[1]}_${String(showingIndex)}_${String(lineIndex)}`;
        push({
          type: 'text',
          sourceObjectId,
          text: line.text,
          sourcePage: pageNumber,
          sourceOrder,
          zOrder: sourceOrder,
          bbox,
          fontName,
          fontSize: size,
          bold,
          italic,
        }, {
          id: sourceObjectId,
          kind: 'text',
          text: line.text,
          bbox,
          lines: [{ text: line.text, bbox, runs: [{ text: line.text, fontName, fontSize: size, bold, italic, bbox }] }],
        });
      });
    }
    pages.push({ pageNumber, width: page.width, height: page.height, blocks });
  });
  const parsed = parsePdfLayoutToIR({
    sourceHash: 'sha256:qxeditor-poc-multi-page-mirror-01',
    pages,
  });
  const document: QXDocumentIR =
    rasters.size === 0
      ? parsed
      : createQXDocumentIR({
          irId: parsed.irId,
          sourceType: parsed.sourceType,
          sourceHash: parsed.sourceHash,
          provenance: parsed.provenance,
          metadata: parsed.metadata,
          language: parsed.language,
          sections: [...parsed.sections],
          blocks: parsed.blocks.map((block) => {
            const raster = rasters.get(block.blockId);
            return raster ? { ...block, raster } : block;
          }),
          assets: [...parsed.assets],
          tables: [...parsed.tables],
          links: [...parsed.links],
          pageMap: [...parsed.pageMap],
          layout: parsed.layout,
          confidence: [...parsed.confidence],
          warnings: [...parsed.warnings],
        });
  return { document, objects };
}
